import React, { useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import uploadIcon from "../../../../asset/images/upload-file.svg";
import view from "../../../../asset/images/view-eyes.svg";
import download from "../../../../asset/images/download-brokrage.svg";
import rejectedIcon from "../../../../asset/images/crossIcon.svg";
import sendBackIcon from "../../../../asset/images/sentback.svg";
import { Dealer_Mapping } from "../../../../Configuration/API_URL";
import axiosInstance from "../../../../CommonMethhods/axiosInstance";
import {
  decryptData,
  encryptData,
  formatDate,
  noDataFound,
  toSentenceCase,
} from "../../../../CommonMethhods/CommonMethods";
import CheckerActionModals from "../CheckerActionModals";
import CheckerPagination from "../CheckerPagination";
import { getReasonTypeLabel } from "../checkerUtils";

// Read-only view of a bulk (BLK) dealer mapping batch, from the checker's
// side - used for Pending / Approved / Rejected / Send Back requests. Pending
// batches additionally get Reject all / Send back / Approve all actions.
// All three actions use the batch-level endpoints (approve-all / reject-all /
// send-back-all).
const SENT_BACK_STATUSES = ["sendback", "send back", "sent_back"];

function CheckerBulkRequestDetails({ batchId, status, rowData, summary, onActionDone }) {
  const [search, setSearch] = useState("");
  const [pendingAction, setPendingAction] = useState(null); // "approve" | "reject" | "sendback"
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);

  const normalizedStatus = (status || "").toLowerCase();

  const getStatusClass = (value = "") => {
    switch (value.toLowerCase()) {
      case "pending":
        return "status-pending";
      // case "partial":
      //   return "status-pending";
      case "approved":
        return "status-approved";
      case "rejected":
        return "status-rejected";
      case "sendback":
      case "send_back":
      case "send back":
        return "status-sendback";
      default:
        return "";
    }
  };

  const getStatusLabel = (value = "") => {
    const normalized = value.toLowerCase();
    // if (normalized === "partial") return "Partial";
    if (normalized === "sendback" || normalized === "send_back") return "Send Back";
    return toSentenceCase(value);
  };

  const requests = rowData?.requests || [];

  const makerName = rowData?.makerName || summary?.makerName || "-";
  const makerId = rowData?.makerId || summary?.makerId || "-";
  const makerLabel = makerName !== "-" ? `${makerName} (${makerId})` : makerId;
  const makerIdLabel = (id) => (makerName !== "-" ? `${id} — ${makerName}` : id);

  const approvalDocLink = rowData?.approvalDocLink || "";
  // Reason type is the maker's category; remarks are what the maker typed for
  // the whole batch (batch-level, not any single row's remarks).
  const firstRequest = requests[0] || {};
  const reasonTypeSource = {
    // reasonType: rowData?.reasonType || summary?.reasonType || firstRequest?.reasonType,
    reasonType: rowData?.reasonType || summary?.reasonType,
    // reason: rowData?.reason || summary?.reason || firstRequest?.reason,
    // Batch-level only: the first row's own reason is not what the maker
    // entered for the batch.
    reason: rowData?.reason || summary?.reason,
  };
  // const reasonText = rowData?.remarks || summary?.remarks || "-";

  // The checker list does not always carry what the maker typed for the whole
  // upload. When it does not, a value every row agrees on is what was entered
  // (a batch-level reason / remarks is applied to the rows as-is); rows that
  // disagree have no single batch value, so nothing is picked from them.
  const distinctRowValues = (field) => [
    ...new Set(requests.map((row) => String(row?.[field] || "").trim()).filter(Boolean)),
  ];
  const rowReasons = distinctRowValues("reason");
  const rowRemarks = distinctRowValues("remarks");

  if (!reasonTypeSource.reasonType && !reasonTypeSource.reason && rowReasons.length === 1) {
    reasonTypeSource.reason = rowReasons[0];
  }
  const batchRemarks = rowData?.remarks || summary?.remarks || (rowRemarks.length === 1 ? rowRemarks[0] : "");
  const reasonText = batchRemarks || "-";

  // const reasonTypeShown =
  //   reasonTypeSource.reasonType || reasonTypeSource.reason
  //     ? getReasonTypeLabel(reasonTypeSource)
  //     : rowReasons.length > 1
  //       ? "Differs by row"
  //       : "-";
  // const remarksShown = batchRemarks || (rowRemarks.length > 1 ? "Differs by row" : "-");
  const reasonTypeShown = getReasonTypeLabel(reasonTypeSource);
  const remarksShown = reasonText;

  const searchText = search.trim().toLowerCase();
  const filteredRequests = searchText
    ? requests.filter((row) =>
        [row?.ucc, row?.clientName, row?.newDealerCode, row?.oldDealerCode]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(searchText))
      )
    : requests;

  const rejectionRemarks = [
    ...new Set(
      requests
        .flatMap((row) =>
          Array.isArray(row?.rejectionReason) ? row.rejectionReason : [row?.rejectionReason]
        )
        .filter(Boolean)
    ),
  ].join(", ");

  const sendBackRemarks = [
    ...new Set(
      requests
        .flatMap((row) =>
          Array.isArray(row?.sendBackReason) ? row.sendBackReason : [row?.sendBackReason]
        )
        .filter(Boolean)
    ),
  ].join(", ");

  const handleViewDoc = () => {
    if (!approvalDocLink) return;
    window.open(approvalDocLink, "_blank", "noopener,noreferrer");
  };

  const handleDownloadDoc = () => {
    if (!approvalDocLink) return;
    window.open(approvalDocLink, "_self", "noopener,noreferrer");
  };

  const isPending = normalizedStatus === "pending";
  const isRejected = normalizedStatus === "rejected";
  const isSendBack = SENT_BACK_STATUSES.includes(normalizedStatus);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const totalCount = filteredRequests.length;
  const paginatedRequests = filteredRequests.slice((page - 1) * limit, page * limit);

  const putEncrypted = async (endpoint, payload) => {
    const response = await axiosInstance.put(endpoint, encryptData(JSON.stringify(payload)), {
      transformRequest: [(data) => data],
    });
    return JSON.parse(decryptData(response.data));
  };

  // Resolves true when the action went through, so the modal can show its
  // success step.
  const handleAction = async (action, reason = "") => {
    try {
      const endpointName = {
        approve: "approve-all",
        reject: "reject-all",
        sendback: "send-back-all",
      }[action];
      const decrypted = await putEncrypted(
        `${Dealer_Mapping.singleMappingSubmit}/${batchId}/${endpointName}`,
        action === "approve" ? {} : { reason }
      );

      if (decrypted?.status !== 0) {
        toast.error(decrypted?.message);
        return false;
      }

      // The batch endpoints run row by row, so some rows can fail while the
      // call itself succeeds.
      const failures = decrypted?.data?.failures;
      if (Array.isArray(failures) && failures.length > 0) {
        toast.warn(`${failures.length} record(s) could not be processed`);
      }
      return true;
    } catch (error) {
      toast.error("Something went wrong");
      return false;
    }
  };

  return (
    <>
      <div className="ckr-section">
        <div className="dlr-request-info">
          <div className="dlr-info-item">
            <span className="dlr-info-label">Request No.</span>
            <span className="dlr-info-value">{batchId}</span>
          </div>

          <div className="dlr-info-item">
            <span className="dlr-info-label">Created by</span>
            <span className="dlr-info-value">{makerLabel}</span>
          </div>

          <div className="dlr-info-item">
            <span className="dlr-info-label">Total Request</span>
            <span className="dlr-info-value">{summary?.count || requests.length || 0}</span>
          </div>

          <div className="dlr-info-item">
            <span className="dlr-info-label">Reason Type</span>
            {/* <span className="dlr-info-value">{getReasonTypeLabel(reasonTypeSource)}</span> */}
            <span className="dlr-info-value">{reasonTypeShown}</span>
          </div>

          <div className="dlr-info-item">
            <span className="dlr-info-label">Reason</span>
            {/* <span className="dlr-info-value">{reasonText}</span> */}
            <span className="dlr-info-value">{remarksShown}</span>
          </div>
        </div>

        {rowData?.approvalDocFilename && (
          <>
            <span className="dlr-sub-title">Attachment</span>

            <div className="rm-attachment dlr-attachment mb-20">
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="file-tooltips">{rowData?.approvalDocFilename}</Tooltip>}
              >
                <div className="attachment-name">
                  <img src={uploadIcon} alt="upload icon" />
                  <span className="file-name">{rowData?.approvalDocFilename}</span>
                </div>
              </OverlayTrigger>

              <div className="attachment-actions">
                <button type="button" onClick={handleViewDoc}>
                  <img src={view} alt="view" className="view-icon" />
                  <span className="btn-title">View</span>
                </button>

                <button type="button" onClick={handleDownloadDoc}>
                  <img src={download} alt="download" />
                  <span className="btn-title">Download</span>
                </button>
              </div>
            </div>
          </>
        )}

        <div className="ckr-section-header">
          <div className="status-content">
            <p className="ckr-section-title">Row Preview valid ({requests.length})</p>
            {status ? (
              <span className={getStatusClass(status)}>{getStatusLabel(status)}</span>
            ) : null}
          </div>

          <div className="d-flex align-items-center header-filter">
            <div className="ckr-search-box focusable-filter">
              <i className="fa fa-search" />
              <input
                type="text"
                placeholder="Search Clients"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="ckr-table-wrapper cdm-table">
          {filteredRequests.length > 0 ? (
            <table className="ckr-table draft-maker">
              <thead>
                <tr>
                  <th>Request Date</th>
                  <th>Maker ID</th>
                  <th>UCC / Client Code</th>
                  <th>Dealer Code</th>
                  <th>Effective Date</th>
                  <th>Reason Type</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRequests.map((row, index) => (
                  <tr key={row?.requestId || index}>
                    <td>{row?.reqDate ? formatDate(row.reqDate) : "-"}</td>
                    <td>{makerIdLabel(row?.makerId || makerId)}</td>
                    <td>
                      {row?.clientName ? `${row.clientName}- ${row?.ucc || ""}` : row?.ucc || "-"}
                    </td>
                    <td>{row?.newDealerCode || "-"}</td>
                    <td>{row?.startDate ? formatDate(row.startDate) : "-"}</td>
                    {/* A row's own reason / remarks can come back blank for a bulk
                        upload, so the batch-level values stand in for it. */}
                    {/* <td>{getReasonTypeLabel(row)}</td> */}
                    {/* <td>
                      {getReasonTypeLabel(
                        row?.reasonType || row?.reason ? row : reasonTypeSource
                      )}
                    </td> */}
                    {/* <td>{row?.remarks || reasonText}</td> */}
                    {/* The batch-level reason / remarks are what the maker entered
                        for the whole upload, so they come first; a row's own
                        value is only used when the batch has none. */}
                    <td>
                      {getReasonTypeLabel(
                        reasonTypeSource.reasonType || reasonTypeSource.reason
                          ? reasonTypeSource
                          : row
                      )}
                    </td>
                    <td>{reasonText !== "-" ? reasonText : row?.remarks || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="step-content report-error-message">{noDataFound()}</div>
          )}

          {totalCount > 0 && (
            <CheckerPagination
              page={page}
              limit={limit}
              totalCount={totalCount}
              onPageChange={setPage}
              onLimitChange={(value) => {
                setLimit(value);
                setPage(1);
              }}
            />
          )}
        </div>
      </div>

      {isRejected && rejectionRemarks && (
        <div className="reject-remark-cont">
          <div>
            <img src={rejectedIcon} alt="rejected" />
          </div>
          <div className="wrap-content">
            <span className="bold-text">Checker Rejection Remarks</span>
            <span className="remarks-val">{rejectionRemarks}</span>
          </div>
        </div>
      )}

      {isSendBack && (
        <>
          {sendBackRemarks && (
            <div className="reject-remark-cont">
              <div>
                <img src={rejectedIcon} alt="remarks" />
              </div>
              <div className="wrap-content">
                <span className="bold-text">Checker Rejection Remarks</span>
                <span className="remarks-val">{sendBackRemarks}</span>
              </div>
            </div>
          )}

          <div className="reject-remark-cont">
            <div>
              <img src={sendBackIcon} alt="return" />
            </div>
            <div className="wrap-content">
              <span className="bold-text">Return to Maker for Review</span>
              <span className="remarks-val">{sendBackRemarks || "-"}</span>
            </div>
          </div>
        </>
      )}

      {isPending && (
        <div className="mapping-footer-actions">
          <button type="button" className="reject-btn filled" onClick={() => setPendingAction("reject")}>
            Reject all
          </button>

          <button type="button" className="back-btn" onClick={() => setPendingAction("sendback")}>
            Send back
          </button>

          <button type="button" className="submit-btns" onClick={() => setPendingAction("approve")}>
            Approve all
          </button>
        </div>
      )}

      {pendingAction && (
        <CheckerActionModals
          action={pendingAction}
          onCancel={() => setPendingAction(null)}
          onSubmit={handleAction}
          onDone={() => {
            setPendingAction(null);
            onActionDone?.();
          }}
        />
      )}
    </>
  );
}

export default CheckerBulkRequestDetails;
